import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearUserAuditTrail,
  exportUserAuditTrail,
  fetchUserAuditTrail,
} from "../../../store/reducers/userAuditSlice";
import { notifyApiError, notifyApiSuccess } from "../../../helpers/apiNotify";
import UserAuditEntryList from "./UserAuditEntryList";
import {
  USER_HISTORY_ACTION_OPTIONS,
  USER_AUDIT_STATUS_OPTIONS,
} from "./userAuditConstants";
import "../applications/auditTrail/AuditTrailModal.css";

/**
 * Per-user History (GET /users/:userId/audit-trail).
 * Admin: any user. Manager: company admins / employees only (API enforces).
 */
const UserHistoryModal = ({ open, onClose, userId, userLabel, accessToken }) => {
  const dispatch = useDispatch();
  const { logs, loading, error, pagination } = useSelector(
    (state) => state.userAudit.userTrail
  );
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open || !userId || !accessToken) return;
    dispatch(
      fetchUserAuditTrail({
        accessToken,
        userId,
        page,
        limit: 20,
        action: action || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
      })
    );
  }, [open, userId, accessToken, page, action, status, from, to, dispatch]);

  useEffect(() => {
    if (!open) {
      setPage(1);
      setAction("");
      setStatus("");
      setFrom("");
      setTo("");
      setDownloading(false);
      dispatch(clearUserAuditTrail());
    }
  }, [open, dispatch]);

  const handleExport = async () => {
    if (!userId || !accessToken || downloading) return;
    setDownloading(true);
    try {
      await exportUserAuditTrail({
        accessToken,
        userId,
        action: action || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
      });
      notifyApiSuccess("Audit trail exported");
    } catch (err) {
      notifyApiError(err, "Failed to export audit trail");
    } finally {
      setDownloading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="audit-modal-overlay" onClick={onClose}>
      <div
        className="audit-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720 }}
      >
        <div className="audit-modal-header">
          <div className="audit-modal-header-row">
            <h2>User History</h2>
            <div className="audit-modal-header-actions">
              <button
                type="button"
                className="audit-download-btn"
                onClick={handleExport}
                disabled={downloading || !userId}
              >
                {downloading ? "Downloading…" : "Export CSV"}
              </button>
              <button
                type="button"
                className="audit-modal-close"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
          <div className="audit-modal-meta">
            <span>
              <strong>User:</strong> {userLabel || userId || "—"}
            </span>
          </div>
        </div>

        <div
          className="audit-modal-filters user-history-filters"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
          }}
        >
          <label>
            Action
            <select
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All actions</option>
              {USER_HISTORY_ACTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              {USER_AUDIT_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            From
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
            />
          </label>
        </div>

        <div className="audit-modal-body">
          <UserAuditEntryList logs={logs} loading={loading} error={error} />
        </div>

        <div className="audit-modal-footer">
          <button
            type="button"
            className="audit-page-btn"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span>
            Page {pagination.currentPage || page} of{" "}
            {pagination.totalPages || 1}
          </span>
          <button
            type="button"
            className="audit-page-btn"
            disabled={
              loading ||
              (pagination.totalPages || 1) <= (pagination.currentPage || page)
            }
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserHistoryModal;
